import clsx from 'clsx'
import {FC, FocusEventHandler, Key, ReactNode} from 'react'
import {ActionMeta, components, GroupBase, Options, OptionsOrGroups} from 'react-select'
import {AsyncPaginate, LoadOptions} from 'react-select-async-paginate'

export type OptionType = {
  label: string
  value: any
}

export type Additional = {
  page: number
}

const defaultAdditional: Additional = {
  page: 1,
}

type Props = {
  key?: Key | null | undefined
  options?: OptionsOrGroups<unknown, GroupBase<unknown>> | undefined
  onChange?: (newValue: unknown, actionMeta: ActionMeta<unknown>) => void
  placeholder?: ReactNode
  isMulti?: any
  isCreatable?: boolean
  onCreateOption?: (inputValue: string) => void | undefined
  value?: any
  isOptionDisabled?: (option: any, selectValue: Options<any>) => boolean
  isClearable?: boolean
  isDisabled?: boolean
  loadOptions: LoadOptions<OptionType, GroupBase<OptionType>, Additional>
  onBlur?: FocusEventHandler<HTMLInputElement>
  type?: 'normal' | 'filled'
  className?: string
  selectKey?: string
  loadOptionsOnMenuOpen?: boolean
  cacheUniqs?: any[]
}

const DropdownSelectAsyncPaginateBody: FC<Props> = ({
  onChange,
  placeholder,
  isMulti,
  value,
  isClearable,
  isDisabled,
  loadOptions,
  onBlur,
  className,
  type = 'normal',
  selectKey,
  loadOptionsOnMenuOpen = false,
  cacheUniqs,
}) => {
  if (!document?.body) {
    return null
  }

  if (type === 'filled') {
    return (
      <AsyncPaginate
        key={selectKey}
        debounceTimeout={1000}
        styles={{
          control: (provided) => ({
            ...provided,
            border: 'none',
            borderRadius: 6,
          }),
          option: (provided) => ({
            ...provided,
            fontSize: 13,
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: 3,
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 3,
          }),
        }}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator: null,
          Control: (props) => (
            <components.Control
              {...props}
              className={clsx(props.className, 'form-select form-select-solid py-2 px-1 border-0')}
            />
          ),
          ClearIndicator: undefined,
          SelectContainer: (props) => (
            <components.SelectContainer {...props} className={clsx(props.className, 'border-0')} />
          ),
          Input: (props) => <components.Input {...props} className='py-0' />,
        }}
        defaultValue={value}
        isMulti={isMulti}
        onChange={onChange}
        placeholder={<span className='custom-dropdown-placeholder fw-semibold'>{placeholder}</span>}
        value={value}
        isSearchable
        defaultOptions
        onBlur={onBlur}
        additional={defaultAdditional}
        isClearable={isClearable}
        loadOptions={loadOptions}
        isDisabled={isDisabled}
        className={className}
        menuPortalTarget={document.body}
        loadOptionsOnMenuOpen={loadOptionsOnMenuOpen}
        cacheUniqs={cacheUniqs}
      />
    )
  }

  return (
    <AsyncPaginate
      key={selectKey}
      debounceTimeout={1000}
      styles={{
        option: (provided) => ({
          ...provided,
          fontSize: 12,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 3,
        }),
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 3,
        }),
      }}
      components={{
        IndicatorSeparator: null,
        Control: (props) => (
          <components.Control
            {...props}
            className={clsx(props.className, 'custom-dropdown-control')}
          />
        ),
        Input: (props) => (
          <components.Input {...props} className={clsx(props.className, 'custom-dropdown-input')} />
        ),
      }}
      defaultValue={value}
      isMulti={isMulti}
      onChange={onChange}
      placeholder={<span className='custom-dropdown-placeholder fw-semibold'>{placeholder}</span>}
      value={value}
      isSearchable
      defaultOptions
      onBlur={onBlur}
      className={className}
      additional={defaultAdditional}
      isClearable={isClearable}
      loadOptions={loadOptions}
      isDisabled={isDisabled}
      menuPortalTarget={document.body}
      loadOptionsOnMenuOpen={loadOptionsOnMenuOpen}
      cacheUniqs={cacheUniqs}
    />
  )
}

export {DropdownSelectAsyncPaginateBody}
